import { useState } from 'react';
import { Card, KpiCard, Modal, SlidePanel, Badge, toast } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { exportCsv } from '@/utils/csv-export';
import styles from './MpRevenue.module.css';

/* ---------- Types ---------- */
interface OtaRow { ota: string; bookings: number; revenue: number; }
interface MonthlyRow { month: string; revenue: number; }
interface PropertyRow { property_name: string; bookings: number; revenue: number; }
interface ExpenseCatRow { category: string; total: number; }
interface PayoutSummary { expected: number; received: number; discrepancy_count: number; pending_count: number; }
interface RevenueData {
  kpis: { totalRevenue: number; totalBookings: number; avgPerNight: number; occupancy: string };
  otaBreakdown: OtaRow[];
  monthlyTrend: MonthlyRow[];
  commissionRates: Record<string, number>;
  propertyBreakdown: PropertyRow[];
  expenseBreakdown: ExpenseCatRow[];
  payoutSummary: PayoutSummary;
}
interface BookingRow {
  id: string; guest_name: string; ota: string; check_in: string; check_out: string; nights: number;
  amount: number; commission_rate: number; commission_amount: number; cleaning_cost: number;
  net_amount: number; property_name: string; other_expenses: number; status: string;
}
interface ExpenseRow {
  id: string; booking_id: string | null; property_id: string | null; category: string;
  description: string | null; amount: number; expense_date: string; property_name: string | null;
}
interface PayoutRow {
  id: string; ota: string; payout_period_start: string; payout_period_end: string;
  expected_amount: number; actual_amount: number | null; payout_date: string | null;
  reference_no: string | null; status: string; notes: string | null;
}
interface PnlData {
  revenue: { byOta: { ota: string; revenue: number; commission: number }[]; byProperty: { property_name: string; revenue: number }[]; total: number };
  expenses: { commission: number; cleaning: number; byCategory: ExpenseCatRow[]; other: number; total: number };
  netProfit: number; profitRate: string; prevMonthRevenue: number;
}

const otaColors: Record<string, string> = { Airbnb: '#FF5A5F', Booking: '#003580', STAY: '#2563EB' };
const categoryLabels: Record<string, string> = { consumables: '消耗品', key_exchange: '鍵交換', maintenance: 'メンテナンス', other: 'その他' };
const tabs = ['概要', '予約別収支', '入金管理', '月次収支表'] as const;
type TabKey = typeof tabs[number];

function fmtMonth(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }

/* ======================================== */
interface ListingItem { id: string; property_id: string; property_name: string; }

export function MpRevenue() {
  const [tab, setTab] = useState<TabKey>('概要');
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [propertyId, setPropertyId] = useState('');
  const { data: listings } = useApi<ListingItem[]>('/tenant/minpaku/listings');
  // property_idで重複排除
  const seen = new Set<string>();
  const minpakuProperties = (listings ?? []).filter((l) => {
    if (seen.has(l.property_id)) return false;
    seen.add(l.property_id);
    return true;
  });

  async function handleCsvExport(currentTab: TabKey, m: string) {
    try {
      const token = localStorage.getItem('mirai_token');
      const user = JSON.parse(localStorage.getItem('mirai_user') || '{}');
      const headers: Record<string, string> = { 'Authorization': `Bearer ${token}`, 'X-Tenant-Slug': user.tenantSlug };

      if (currentTab === '予約別収支' || currentTab === '概要') {
        const res = await fetch(`/api/tenant/minpaku/revenue/bookings?month=${m}`, { headers });
        const rows = await res.json();
        exportCsv(`OTA収支_予約別_${m}.csv`, [
          { key: 'check_in', label: '日付' },
          { key: 'guest_name', label: 'ゲスト' },
          { key: 'ota', label: 'OTA' },
          { key: 'property_name', label: '物件' },
          { key: 'nights', label: '泊数' },
          { key: 'amount', label: '売上' },
          { key: 'commission_rate', label: '手数料率', format: (r: Record<string, unknown>) => `${((Number(r.commission_rate) || 0) * 100).toFixed(1)}%` },
          { key: 'commission_amount', label: '手数料額' },
          { key: 'cleaning_cost', label: '清掃費' },
          { key: 'net_amount', label: '純利益' },
        ], rows);
        toast(`${rows.length}件の予約データをCSV出力しました`, 'success');
      } else if (currentTab === '入金管理') {
        const res = await fetch(`/api/tenant/minpaku/payouts?month=${m}`, { headers });
        const rows = await res.json();
        exportCsv(`OTA入金_${m}.csv`, [
          { key: 'ota', label: 'OTA' },
          { key: 'payout_period_start', label: '対象期間開始' },
          { key: 'payout_period_end', label: '対象期間終了' },
          { key: 'expected_amount', label: '入金予定額' },
          { key: 'actual_amount', label: '実入金額' },
          { key: 'payout_date', label: '入金日' },
          { key: 'reference_no', label: '参照番号' },
          { key: 'status', label: 'ステータス' },
        ], rows);
        toast(`${rows.length}件の入金データをCSV出力しました`, 'success');
      } else if (currentTab === '月次収支表') {
        const res = await fetch(`/api/tenant/minpaku/revenue/pnl?month=${m}`, { headers });
        const data = await res.json();
        if (!data) { toast('データがありません', 'error'); return; }
        const pnlRows: Record<string, unknown>[] = [];
        for (const o of data.revenue.byOta) pnlRows.push({ item: `${o.ota} 売上`, amount: Number(o.revenue) });
        pnlRows.push({ item: '収入合計', amount: data.revenue.total });
        pnlRows.push({ item: 'OTA手数料', amount: -data.expenses.commission });
        pnlRows.push({ item: '清掃費', amount: -data.expenses.cleaning });
        for (const e of data.expenses.byCategory) pnlRows.push({ item: e.category, amount: -Number(e.total) });
        pnlRows.push({ item: '支出合計', amount: -data.expenses.total });
        pnlRows.push({ item: '純利益', amount: data.netProfit });
        exportCsv(`OTA月次収支_${m}.csv`, [
          { key: 'item', label: '項目' },
          { key: 'amount', label: '金額' },
        ], pnlRows);
        toast('月次収支表をCSV出力しました', 'success');
      }
    } catch { toast('CSV出力に失敗しました', 'error'); }
  }

  return (
    <div className={styles.page}>
      {/* Month selector + Tabs */}
      <div className={styles.toolbar}>
        <input type="month" className={styles.monthInput} value={month} onChange={(e) => setMonth(e.target.value)} />
        <select className={styles.filterSelect} value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
          <option value="">全物件</option>
          {minpakuProperties.map((p) => <option key={p.property_id} value={p.property_id}>{p.property_name}</option>)}
        </select>
        <div className={styles.toolbarActions}>
          <button className={styles.btn} onClick={() => { setTab('月次収支表'); setTimeout(() => window.print(), 300); }}>📥 PDF出力</button>
          <button className={styles.btn} onClick={() => handleCsvExport(tab, month)}>📤 CSV</button>
        </div>
        <span className={styles.spacer} />
        <div className={styles.tabs}>
          {tabs.map((t) => (
            <button key={t} className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
      </div>

      {tab === '概要' && <OverviewTab month={month} propertyId={propertyId} />}
      {tab === '予約別収支' && <BookingsTab month={month} propertyId={propertyId} />}
      {tab === '入金管理' && <PayoutsTab month={month} />}
      {tab === '月次収支表' && <PnlTab month={month} propertyId={propertyId} />}
    </div>
  );
}

/* ======== Tab 1: 概要 ======== */
function OverviewTab({ month, propertyId }: { month: string; propertyId: string }) {
  const pq = propertyId ? `&property_id=${propertyId}` : '';
  const { data, loading } = useApi<RevenueData>(`/tenant/minpaku/revenue?month=${month}${pq}`);
  if (loading) return <div>読み込み中...</div>;

  const totalRevenue = data?.kpis.totalRevenue ?? 0;
  const totalBookings = data?.kpis.totalBookings ?? 0;
  const otaBreakdown = data?.otaBreakdown ?? [];
  const commissionRates = data?.commissionRates ?? {};
  const defaultRate = commissionRates['default'] ?? 0.1;
  const propertyBreakdown = data?.propertyBreakdown ?? [];
  const payoutSummary = data?.payoutSummary;

  const totalCommission = otaBreakdown.reduce((sum, o) => {
    const rate = commissionRates[o.ota] ?? defaultRate;
    return sum + Math.round(Number(o.revenue) * rate);
  }, 0);

  const totalCleaningCost = (data?.expenseBreakdown ?? []).reduce((s, e) => s + Number(e.total), 0) || totalBookings * 5000;
  const netProfit = totalRevenue - totalCommission - totalCleaningCost;
  const profitRate = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : '0.0';

  const avgCommRate = totalRevenue > 0 ? Math.round((totalCommission / totalRevenue) * 100) : 0;

  const maxOtaRevenue = Math.max(...otaBreakdown.map((o) => Number(o.revenue)), 1);
  const maxPropRevenue = Math.max(...propertyBreakdown.map((p) => Number(p.revenue)), 1);

  return (
    <>
      <div className={styles.kpiRow}>
        <div className={styles.kpiCustom}>
          <div className={styles.kpiLabel}>OTA総売上（今月）</div>
          <div className={styles.kpiValue}>¥{totalRevenue.toLocaleString()}</div>
          <div className={styles.kpiDelta} style={{ color: 'var(--green)' }}>↑ {totalBookings}件</div>
        </div>
        <div className={styles.kpiCustom}>
          <div className={styles.kpiLabel}>OTA手数料合計</div>
          <div className={styles.kpiValue} style={{ color: 'var(--red)' }}>−¥{totalCommission.toLocaleString()}</div>
          <div className={styles.kpiDelta} style={{ color: 'var(--ink3)' }}>平均 {avgCommRate}%</div>
        </div>
        <div className={styles.kpiCustom}>
          <div className={styles.kpiLabel}>清掃費合計</div>
          <div className={styles.kpiValue} style={{ color: 'var(--amber)' }}>−¥{totalCleaningCost.toLocaleString()}</div>
          <div className={styles.kpiDelta} style={{ color: 'var(--ink3)' }}>{totalBookings}件 × 平均¥{totalBookings > 0 ? Math.round(totalCleaningCost / totalBookings).toLocaleString() : '0'}</div>
        </div>
        <div className={styles.kpiCustom}>
          <div className={styles.kpiLabel}>純利益（概算）</div>
          <div className={styles.kpiValue} style={{ color: 'var(--green)' }}>¥{netProfit.toLocaleString()}</div>
          <div className={styles.kpiDelta} style={{ color: 'var(--green)' }}>↑ 利益率 {profitRate}%</div>
        </div>
        <KpiCard label="入金状況" value={`¥${Number(payoutSummary?.received ?? 0).toLocaleString()}`} delta={`未入金 ${payoutSummary?.pending_count ?? 0}件`} trend={(payoutSummary?.pending_count ?? 0) > 0 ? 'down' : 'up'} />
      </div>

      <div className={styles.twoCol}>
        <Card title="📊 OTA別売上内訳">
          <div className={styles.barChart}>
            {otaBreakdown.map((o) => {
              const rev = Number(o.revenue);
              const pct = (rev / maxOtaRevenue) * 100;
              return (
                <div key={o.ota} className={styles.barRow}>
                  <span className={styles.barLabel}>{o.ota}</span>
                  <div className={styles.barTrack}>
                    <div className={styles.barFill} style={{ width: `${pct}%`, background: otaColors[o.ota] ?? 'var(--brand)' }}>
                      <span className={styles.barValue}>¥{rev.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {otaBreakdown.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>データなし</div>}
          </div>
        </Card>

        <Card title="🏠 物件別売上（上位4件）">
          <div className={styles.barChart}>
            {propertyBreakdown.map((p) => {
              const rev = Number(p.revenue);
              const pct = (rev / maxPropRevenue) * 100;
              return (
                <div key={p.property_name} className={styles.barRow}>
                  <span className={styles.barLabel}>{p.property_name}</span>
                  <div className={styles.barTrack}>
                    <div className={styles.barFill} style={{ width: `${pct}%`, background: 'var(--brand)' }}>
                      <span className={styles.barValue}>¥{rev.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {propertyBreakdown.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>データなし</div>}
          </div>
        </Card>
      </div>

      <Card title="OTA精算一覧">
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>OTA</th>
              <th className={styles.th}>宿泊数</th>
              <th className={styles.th}>総売上</th>
              <th className={styles.th}>手数料</th>
              <th className={styles.th}>精算額</th>
              <th className={styles.th}>ステータス</th>
            </tr>
          </thead>
          <tbody>
            {otaBreakdown.map((o) => {
              const rev = Number(o.revenue);
              const rate = commissionRates[o.ota] ?? defaultRate;
              const commission = Math.round(rev * rate);
              const net = rev - commission;
              return (
                <tr key={o.ota}>
                  <td className={styles.td} style={{ fontWeight: 600 }}>{o.ota}</td>
                  <td className={styles.tdMono}>{o.bookings}泊</td>
                  <td className={styles.tdMono}>¥{rev.toLocaleString()}</td>
                  <td className={styles.tdMono} style={{ color: 'var(--red)' }}>-¥{commission.toLocaleString()}</td>
                  <td className={styles.tdMono} style={{ fontWeight: 700 }}>¥{net.toLocaleString()}</td>
                  <td className={styles.td}><span className={`${styles.statusBadge} ${styles.statusSettled}`}>精算済</span></td>
                </tr>
              );
            })}
            {otaBreakdown.length === 0 && <tr><td className={styles.td} colSpan={6} style={{ textAlign: 'center', color: 'var(--ink3)' }}>データなし</td></tr>}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/* ======== Tab 2: 予約別収支 ======== */
function BookingsTab({ month, propertyId }: { month: string; propertyId: string }) {
  const pq = propertyId ? `&property_id=${propertyId}` : '';
  const { data: bookings, loading, refetch } = useApi<BookingRow[]>(`/tenant/minpaku/revenue/bookings?month=${month}${pq}`);
  const { data: expenses, refetch: refetchExpenses } = useApi<ExpenseRow[]>(`/tenant/minpaku/expenses?month=${month}${pq}`);
  const [selected, setSelected] = useState<BookingRow | null>(null);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expForm, setExpForm] = useState({ category: 'consumables', description: '', amount: '', expense_date: '', property_id: '', booking_id: '' });

  if (loading) return <div>読み込み中...</div>;
  const rows = bookings ?? [];

  const totalRevenue = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const totalCommission = rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
  const totalCleaning = rows.reduce((s, r) => s + (r.cleaning_cost || 0), 0);
  const totalNet = rows.reduce((s, r) => s + (r.net_amount || 0), 0);

  const kpis = [
    { label: '売上合計', value: `¥${totalRevenue.toLocaleString()}`, delta: `${rows.length}件`, trend: 'up' as const },
    { label: '手数料合計', value: `¥${totalCommission.toLocaleString()}`, delta: '', trend: 'down' as const },
    { label: '清掃費合計', value: `¥${totalCleaning.toLocaleString()}`, delta: '', trend: 'flat' as const },
    { label: '純利益合計', value: `¥${totalNet.toLocaleString()}`, delta: '', trend: totalNet > 0 ? 'up' as const : 'down' as const },
  ];

  async function handleExpenseSubmit() {
    if (!expForm.amount || !expForm.expense_date) { toast('金額と日付は必須です', 'error'); return; }
    await api('/tenant/minpaku/expenses', { method: 'POST', body: JSON.stringify({ ...expForm, amount: Number(expForm.amount) }) });
    toast('経費を登録しました', 'success');
    setExpenseOpen(false);
    setExpForm({ category: 'consumables', description: '', amount: '', expense_date: '', property_id: '', booking_id: '' });
    refetchExpenses();
  }

  return (
    <>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <Card title="予約別収支明細">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className={styles.btnPrimary} onClick={() => setExpenseOpen(true)}>+ 経費登録</button>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>日付</th>
              <th className={styles.th}>ゲスト</th>
              <th className={styles.th}>OTA</th>
              <th className={styles.th}>物件</th>
              <th className={styles.th}>売上</th>
              <th className={styles.th}>手数料</th>
              <th className={styles.th}>清掃費</th>
              <th className={styles.th}>純利益</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={styles.clickRow} onClick={() => setSelected(r)}>
                <td className={styles.td}>{r.check_in}</td>
                <td className={styles.td}>{r.guest_name}</td>
                <td className={styles.td}><Badge variant={r.ota === 'Airbnb' ? 'red' : r.ota === 'Booking' ? 'blue' : 'purple'}>{r.ota}</Badge></td>
                <td className={styles.td}>{r.property_name}</td>
                <td className={styles.tdMono}>¥{(r.amount || 0).toLocaleString()}</td>
                <td className={styles.tdMono} style={{ color: 'var(--red)' }}>-¥{(r.commission_amount || 0).toLocaleString()}</td>
                <td className={styles.tdMono} style={{ color: 'var(--amber)' }}>-¥{(r.cleaning_cost || 0).toLocaleString()}</td>
                <td className={styles.tdMono} style={{ fontWeight: 700 }}>¥{(r.net_amount || 0).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className={styles.td} colSpan={8} style={{ textAlign: 'center', color: 'var(--ink3)' }}>データなし</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* 経費一覧 */}
      {(expenses ?? []).length > 0 && (
        <Card title="経費一覧">
          <table className={styles.table}>
            <thead><tr><th className={styles.th}>日付</th><th className={styles.th}>カテゴリ</th><th className={styles.th}>物件</th><th className={styles.th}>説明</th><th className={styles.th}>金額</th></tr></thead>
            <tbody>
              {(expenses ?? []).map((e) => (
                <tr key={e.id}>
                  <td className={styles.td}>{e.expense_date}</td>
                  <td className={styles.td}><Badge variant="gray">{categoryLabels[e.category] ?? e.category}</Badge></td>
                  <td className={styles.td}>{e.property_name ?? '—'}</td>
                  <td className={styles.td}>{e.description ?? '—'}</td>
                  <td className={styles.tdMono}>¥{(e.amount || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* 予約詳細パネル */}
      <SlidePanel open={!!selected} onClose={() => setSelected(null)} title="予約収支詳細">
        {selected && (
          <>
            <PanelSection title="予約情報">
              <SpecGrid>
                <SpecCell label="ゲスト" value={selected.guest_name} />
                <SpecCell label="OTA" value={selected.ota} />
                <SpecCell label="物件" value={selected.property_name} />
                <SpecCell label="チェックイン" value={selected.check_in} />
                <SpecCell label="チェックアウト" value={selected.check_out} />
                <SpecCell label="泊数" value={`${selected.nights}泊`} />
              </SpecGrid>
            </PanelSection>
            <PanelSection title="収支内訳">
              <SpecGrid>
                <SpecCell label="売上" value={`¥${(selected.amount || 0).toLocaleString()}`} mono />
                <SpecCell label="手数料率" value={`${((Number(selected.commission_rate) || 0) * 100).toFixed(1)}%`} mono />
                <SpecCell label="手数料額" value={`-¥${(selected.commission_amount || 0).toLocaleString()}`} mono />
                <SpecCell label="清掃費" value={`-¥${(selected.cleaning_cost || 0).toLocaleString()}`} mono />
                <SpecCell label="その他経費" value={`-¥${(selected.other_expenses || 0).toLocaleString()}`} mono />
                <SpecCell label="純利益" value={`¥${(selected.net_amount || 0).toLocaleString()}`} mono />
              </SpecGrid>
            </PanelSection>
          </>
        )}
      </SlidePanel>

      {/* 経費登録モーダル */}
      <Modal open={expenseOpen} onClose={() => setExpenseOpen(false)} title="経費登録" footer={<><button className={styles.btn} onClick={() => setExpenseOpen(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleExpenseSubmit}>登録</button></>}>
        <div className={styles.formGrid}>
          <label className={styles.formLabel}>カテゴリ
            <select className={styles.formInput} value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })}>
              <option value="consumables">消耗品</option>
              <option value="key_exchange">鍵交換</option>
              <option value="maintenance">メンテナンス</option>
              <option value="other">その他</option>
            </select>
          </label>
          <label className={styles.formLabel}>金額 (円)
            <input className={styles.formInput} type="number" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} />
          </label>
          <label className={styles.formLabel}>日付
            <input className={styles.formInput} type="date" value={expForm.expense_date} onChange={(e) => setExpForm({ ...expForm, expense_date: e.target.value })} />
          </label>
          <label className={styles.formLabel}>説明
            <input className={styles.formInput} type="text" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} placeholder="アメニティセット補充など" />
          </label>
        </div>
      </Modal>
    </>
  );
}

/* ======== Tab 3: 入金管理 ======== */
function PayoutsTab({ month }: { month: string }) {
  const { data: payouts, loading, refetch } = useApi<PayoutRow[]>(`/tenant/minpaku/payouts?month=${month}`);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [editPayout, setEditPayout] = useState<PayoutRow | null>(null);
  const [form, setForm] = useState({ ota: 'Airbnb', payout_period_start: '', payout_period_end: '', expected_amount: '', actual_amount: '', payout_date: '', reference_no: '', notes: '' });

  if (loading) return <div>読み込み中...</div>;
  const rows = payouts ?? [];

  const totalExpected = rows.reduce((s, r) => s + (r.expected_amount || 0), 0);
  const totalReceived = rows.reduce((s, r) => s + (r.actual_amount || 0), 0);
  const pendingCount = rows.filter((r) => r.status === 'pending').length;
  const discrepancyCount = rows.filter((r) => r.status === 'discrepancy').length;

  const kpis = [
    { label: '入金予定額', value: `¥${totalExpected.toLocaleString()}`, delta: `${rows.length}件`, trend: 'flat' as const },
    { label: '入金済額', value: `¥${totalReceived.toLocaleString()}`, delta: '', trend: 'up' as const },
    { label: '未入金', value: `¥${(totalExpected - totalReceived).toLocaleString()}`, delta: `${pendingCount}件`, trend: pendingCount > 0 ? 'down' as const : 'up' as const },
    { label: '差異件数', value: `${discrepancyCount}件`, delta: '', trend: discrepancyCount > 0 ? 'down' as const : 'up' as const },
  ];

  const statusVariant = (s: string) => {
    if (s === 'received') return 'green';
    if (s === 'pending') return 'amber';
    if (s === 'discrepancy') return 'red';
    return 'gray';
  };
  const statusLabel: Record<string, string> = { pending: '未入金', received: '入金済', partial: '一部入金', discrepancy: '差異あり', resolved: '解決済' };

  async function handlePayoutSubmit() {
    if (!form.ota || !form.payout_period_start || !form.payout_period_end) { toast('OTA・期間は必須です', 'error'); return; }
    const body = { ...form, expected_amount: Number(form.expected_amount) || 0, actual_amount: form.actual_amount ? Number(form.actual_amount) : null, status: form.actual_amount ? 'received' : 'pending' };
    await api('/tenant/minpaku/payouts', { method: 'POST', body: JSON.stringify(body) });
    toast('入金レコードを登録しました', 'success');
    setPayoutOpen(false);
    setForm({ ota: 'Airbnb', payout_period_start: '', payout_period_end: '', expected_amount: '', actual_amount: '', payout_date: '', reference_no: '', notes: '' });
    refetch();
  }

  async function handleMarkReceived() {
    if (!editPayout) return;
    const actual = prompt('実入金額を入力してください', String(editPayout.expected_amount));
    if (actual === null) return;
    const actualNum = Number(actual);
    const status = actualNum === editPayout.expected_amount ? 'received' : actualNum > 0 ? 'discrepancy' : 'pending';
    await api(`/tenant/minpaku/payouts/${editPayout.id}`, { method: 'PATCH', body: JSON.stringify({ actual_amount: actualNum, payout_date: new Date().toISOString().slice(0, 10), status }) });
    toast('入金を記録しました', 'success');
    setEditPayout(null);
    refetch();
  }

  async function handleReconcile() {
    if (!editPayout) return;
    const res = await api<{ matched: number; expectedAmount: number }>(`/tenant/minpaku/payouts/${editPayout.id}/reconcile`, { method: 'POST' });
    toast(`${res.matched}件の予約をマッチしました（予定額: ¥${res.expectedAmount.toLocaleString()}）`, 'success');
    setEditPayout(null);
    refetch();
  }

  return (
    <>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <Card title="入金レコード">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className={styles.btnPrimary} onClick={() => setPayoutOpen(true)}>+ 入金登録</button>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>OTA</th>
              <th className={styles.th}>対象期間</th>
              <th className={styles.th}>入金予定額</th>
              <th className={styles.th}>実入金額</th>
              <th className={styles.th}>入金日</th>
              <th className={styles.th}>参照番号</th>
              <th className={styles.th}>ステータス</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={styles.clickRow} onClick={() => setEditPayout(r)}>
                <td className={styles.td} style={{ fontWeight: 600 }}>{r.ota}</td>
                <td className={styles.td}>{r.payout_period_start} ~ {r.payout_period_end}</td>
                <td className={styles.tdMono}>¥{(r.expected_amount || 0).toLocaleString()}</td>
                <td className={styles.tdMono}>{r.actual_amount != null ? `¥${r.actual_amount.toLocaleString()}` : '—'}</td>
                <td className={styles.td}>{r.payout_date ?? '—'}</td>
                <td className={styles.td}>{r.reference_no ?? '—'}</td>
                <td className={styles.td}><Badge variant={statusVariant(r.status)}>{statusLabel[r.status] ?? r.status}</Badge></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className={styles.td} colSpan={7} style={{ textAlign: 'center', color: 'var(--ink3)' }}>データなし</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* 入金詳細パネル */}
      <SlidePanel open={!!editPayout} onClose={() => setEditPayout(null)} title="入金詳細" actions={
        <>
          <button className={styles.btn} onClick={handleReconcile}>消込実行</button>
          <button className={styles.btnPrimary} onClick={handleMarkReceived}>入金記録</button>
        </>
      }>
        {editPayout && (
          <>
            <PanelSection title="入金情報">
              <SpecGrid>
                <SpecCell label="OTA" value={editPayout.ota} />
                <SpecCell label="対象期間" value={`${editPayout.payout_period_start} ~ ${editPayout.payout_period_end}`} />
                <SpecCell label="入金予定額" value={`¥${(editPayout.expected_amount || 0).toLocaleString()}`} mono />
                <SpecCell label="実入金額" value={editPayout.actual_amount != null ? `¥${editPayout.actual_amount.toLocaleString()}` : '—'} mono />
                <SpecCell label="入金日" value={editPayout.payout_date ?? '—'} />
                <SpecCell label="参照番号" value={editPayout.reference_no ?? '—'} />
                <SpecCell label="ステータス" value={statusLabel[editPayout.status] ?? editPayout.status} />
              </SpecGrid>
            </PanelSection>
            {editPayout.actual_amount != null && editPayout.expected_amount !== editPayout.actual_amount && (
              <PanelSection title="差異情報">
                <div className={styles.discrepancy}>
                  差額: ¥{Math.abs(editPayout.expected_amount - (editPayout.actual_amount || 0)).toLocaleString()}
                  {editPayout.actual_amount! > editPayout.expected_amount ? ' (超過)' : ' (不足)'}
                </div>
              </PanelSection>
            )}
          </>
        )}
      </SlidePanel>

      {/* 入金登録モーダル */}
      <Modal open={payoutOpen} onClose={() => setPayoutOpen(false)} title="入金登録" footer={<><button className={styles.btn} onClick={() => setPayoutOpen(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handlePayoutSubmit}>登録</button></>}>
        <div className={styles.formGrid}>
          <label className={styles.formLabel}>OTA
            <select className={styles.formInput} value={form.ota} onChange={(e) => setForm({ ...form, ota: e.target.value })}>
              <option value="Airbnb">Airbnb</option>
              <option value="Booking">Booking.com</option>
              <option value="STAY">STAY JAPAN</option>
            </select>
          </label>
          <label className={styles.formLabel}>対象期間（開始）
            <input className={styles.formInput} type="date" value={form.payout_period_start} onChange={(e) => setForm({ ...form, payout_period_start: e.target.value })} />
          </label>
          <label className={styles.formLabel}>対象期間（終了）
            <input className={styles.formInput} type="date" value={form.payout_period_end} onChange={(e) => setForm({ ...form, payout_period_end: e.target.value })} />
          </label>
          <label className={styles.formLabel}>入金予定額
            <input className={styles.formInput} type="number" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} />
          </label>
          <label className={styles.formLabel}>実入金額（入金済の場合）
            <input className={styles.formInput} type="number" value={form.actual_amount} onChange={(e) => setForm({ ...form, actual_amount: e.target.value })} />
          </label>
          <label className={styles.formLabel}>入金日
            <input className={styles.formInput} type="date" value={form.payout_date} onChange={(e) => setForm({ ...form, payout_date: e.target.value })} />
          </label>
          <label className={styles.formLabel}>参照番号
            <input className={styles.formInput} type="text" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="AB-2026-04-001" />
          </label>
        </div>
      </Modal>
    </>
  );
}

/* ======== Tab 4: 月次収支表 ======== */
function PnlTab({ month, propertyId }: { month: string; propertyId: string }) {
  const pq = propertyId ? `&property_id=${propertyId}` : '';
  const { data, loading } = useApi<PnlData>(`/tenant/minpaku/revenue/pnl?month=${month}${pq}`);
  if (loading) return <div>読み込み中...</div>;
  if (!data) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>データなし</div>;

  const prevDelta = data.prevMonthRevenue > 0 ? (((data.revenue.total - data.prevMonthRevenue) / data.prevMonthRevenue) * 100).toFixed(1) : '—';

  return (
    <>
      <Card title={`月次収支表 — ${month}`}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className={styles.btn} onClick={() => window.print()}>印刷</button>
        </div>

        <table className={styles.pnlTable}>
          <thead>
            <tr><th className={styles.pnlTh} colSpan={2}>項目</th><th className={styles.pnlTh}>金額</th></tr>
          </thead>
          <tbody>
            {/* 収入セクション */}
            <tr className={styles.pnlSection}><td colSpan={3}>【収入】</td></tr>
            {data.revenue.byOta.map((o) => (
              <tr key={o.ota}><td className={styles.pnlIndent}>{o.ota} 売上</td><td></td><td className={styles.pnlMono}>¥{Number(o.revenue).toLocaleString()}</td></tr>
            ))}
            <tr className={styles.pnlSubtotal}><td>収入合計</td><td></td><td className={styles.pnlMono}>¥{data.revenue.total.toLocaleString()}</td></tr>

            {/* 支出セクション */}
            <tr className={styles.pnlSection}><td colSpan={3}>【支出】</td></tr>
            <tr><td className={styles.pnlIndent}>OTA手数料</td><td></td><td className={styles.pnlMono} style={{ color: 'var(--red)' }}>-¥{data.expenses.commission.toLocaleString()}</td></tr>
            <tr><td className={styles.pnlIndent}>清掃費</td><td></td><td className={styles.pnlMono} style={{ color: 'var(--red)' }}>-¥{data.expenses.cleaning.toLocaleString()}</td></tr>
            {data.expenses.byCategory.map((e) => (
              <tr key={e.category}><td className={styles.pnlIndent}>{categoryLabels[e.category] ?? e.category}</td><td></td><td className={styles.pnlMono} style={{ color: 'var(--red)' }}>-¥{Number(e.total).toLocaleString()}</td></tr>
            ))}
            <tr className={styles.pnlSubtotal}><td>支出合計</td><td></td><td className={styles.pnlMono} style={{ color: 'var(--red)' }}>-¥{data.expenses.total.toLocaleString()}</td></tr>

            {/* 利益 */}
            <tr className={styles.pnlTotal}>
              <td>純利益</td>
              <td style={{ fontSize: 11, color: 'var(--ink3)' }}>利益率 {data.profitRate}%</td>
              <td className={styles.pnlMono} style={{ fontWeight: 800, color: data.netProfit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                ¥{data.netProfit.toLocaleString()}
              </td>
            </tr>

            {/* 前月比 */}
            <tr className={styles.pnlNote}>
              <td colSpan={3}>
                前月売上: ¥{data.prevMonthRevenue.toLocaleString()} / 前月比: {prevDelta}%
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* 物件別内訳 */}
      <Card title="物件別売上内訳">
        <table className={styles.table}>
          <thead><tr><th className={styles.th}>物件名</th><th className={styles.th}>売上</th></tr></thead>
          <tbody>
            {data.revenue.byProperty.map((p) => (
              <tr key={p.property_name}>
                <td className={styles.td}>{p.property_name}</td>
                <td className={styles.tdMono}>¥{Number(p.revenue).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}
